import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import './CashOutHistory.scss';

const CashOutHistory = ({ cashOutHistory = [], playerId, onDeleteCashOut }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCashOut, setSelectedCashOut] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleMenuOpen = (event, cashOut) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedCashOut(cashOut);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCashOut(null);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    setAnchorEl(null); // Just close the menu but keep selectedCashOut
  };

  const handleDeleteConfirm = () => {
    if (selectedCashOut && onDeleteCashOut) {
      onDeleteCashOut(playerId, selectedCashOut.id);
    }
    setDeleteDialogOpen(false);
    setSelectedCashOut(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedCashOut(null);
  };

  if (cashOutHistory.length === 0) {
    return (
      <Card className="cash-out-history empty">
        <CardContent>
          <Box className="empty-state">
            <AccountBalanceWalletIcon className="empty-icon" />
            <Typography variant="h6" className="empty-title">
              No Cash Outs Yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Cash out amounts will appear here
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const sortedHistory = [...cashOutHistory].sort((a, b) => 
    new Date(b.timestamp) - new Date(a.timestamp)
  );

  return (
    <>
    <Card className="cash-out-history">
      <CardContent>
        <Box className="history-header">
          <AccountBalanceWalletIcon className="history-icon" />
          <Typography variant="h6">
            Cash Out History ({cashOutHistory.length})
          </Typography>
        </Box>
        
        <Divider className="header-divider" />
        
        <Box className="history-list">
          {sortedHistory.map((cashOut, index) => (
            <Box key={cashOut.id} className="history-item">
              <Box className="item-header">
                <Box className="item-info">
                  <Chip 
                    label={formatCurrency(cashOut.amount)}
                    color="success"
                    className="amount-chip"
                  />
                  <Typography variant="body2" color="text.secondary" className="date">
                    {formatDate(cashOut.timestamp)}
                  </Typography>
                </Box>
                {onDeleteCashOut && (
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, cashOut)}
                    className="menu-button"
                  >
                    <MoreVertIcon />
                  </IconButton>
                )}
              </Box>
              
              {cashOut.note && (
                <Typography variant="body2" className="note">
                  {cashOut.note}
                </Typography>
              )}
              
              {index < sortedHistory.length - 1 && (
                <Divider className="item-divider" />
              )}
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>

    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleMenuClose}
    >
      <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
        <DeleteIcon sx={{ mr: 1 }} />
        Delete Cash Out
      </MenuItem>
    </Menu>

    <Dialog
      open={deleteDialogOpen}
      onClose={handleDeleteCancel}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          <Typography variant="h6">
            Delete Cash Out
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1">
          Are you sure you want to delete this cash out of{' '}
          <strong>{selectedCashOut && formatCurrency(selectedCashOut.amount)}</strong>?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This action cannot be undone and will adjust the player's total cash out amount.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDeleteCancel} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={handleDeleteConfirm} 
          color="error"
          variant="contained"
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default CashOutHistory;